/**
 * GeoTIFF loading utilities.
 *
 * Two entry points:
 *   - `decodeGeoTiffTexture()` — lightweight: returns RGBA pixels + dimensions for display.
 *     Used by the Image behavior at load time. Does NOT retain raw band data in memory.
 *   - `decodeGeoTiff()` — full decode: returns raw band data, geo-referencing metadata,
 *     and RGBA pixels. Used on-demand by tooling (decoded from the ResourceManager blob).
 */

import type { GeoTIFFImage, TypedArray, ReadRasterResult } from 'geotiff';
import { Logger } from './Logger';


// ── Public types ──────────────────────────────────────────────────────

/** Metadata extracted from a GeoTIFF image. */
export interface GeoTiffMetadata {
    width: number;
    height: number;
    /** Number of raster bands (e.g. 1 for DEM, 3 for RGB, 4 for RGBA). */
    samplesPerPixel: number;
    /** Bits per sample per band. */
    bitsPerSample: number;
    /** Geo-referencing keys (EPSG codes, etc.), if present. */
    geoKeys: Record<string, unknown> | null;
    /** The NODATA sentinel value, if declared. */
    noData: number | null;
    /** Image origin in CRS coordinates [x, y, z], if available. */
    origin: number[] | null;
    /** Pixel resolution in CRS units [x, y, z], if available. */
    resolution: number[] | null;
    /** Bounding box in CRS coordinates [minX, minY, maxX, maxY], if available. */
    bbox: number[] | null;
}

/** Full result of decoding a GeoTIFF blob (bands + metadata + RGBA). */
export interface GeoTiffResult {
    /** RGBA pixels ready for texture upload (width × height × 4). */
    rgba: Uint8ClampedArray;
    /** Per-band raw raster data in original numeric type. */
    bands: TypedArray[];
    /** Image metadata and geo-referencing info. */
    metadata: GeoTiffMetadata;
}

/** @internal Lightweight result for texture display only. */
export interface GeoTiffTextureResult {
    /** RGBA pixels ready for texture upload (width × height × 4). */
    rgba: Uint8ClampedArray;
    width: number;
    height: number;
    /** True if the TIFF has geo-referencing (bounding box / affine transform). */
    isGeoReferenced: boolean;
}

// ── Internal helpers ──────────────────────────────────────────────────

async function openImage(blob: Blob): Promise<GeoTIFFImage> {
    const { fromBlob } = await import('geotiff');
    const tiff = await fromBlob(blob);
    return tiff.getImage();
}

/**
 * Map a single-band raster to a grayscale RGBA image, stretching the value
 * range across 0-255 for visualization.  NODATA pixels become transparent.
 * @internal Exported for testing.
 */
export function singleBandToRGBA(
    band: TypedArray,
    width: number,
    height: number,
    noData: number | null,
): Uint8ClampedArray {
    const rgba = new Uint8ClampedArray(width * height * 4);

    // Find value range (ignoring NODATA)
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < band.length; i++) {
        const v = band[i];
        if (noData !== null && v === noData) continue;
        if (v < min) min = v;
        if (v > max) max = v;
    }
    if (!isFinite(min)) { min = 0; max = 1; }

    const range = max - min || 1;
    for (let i = 0; i < band.length; i++) {
        const v = band[i];
        const idx = i * 4;
        if (noData !== null && v === noData) {
            rgba[idx] = 0;
            rgba[idx + 1] = 0;
            rgba[idx + 2] = 0;
            rgba[idx + 3] = 0;
        } else {
            const norm = Math.round(((v - min) / range) * 255);
            rgba[idx] = norm;
            rgba[idx + 1] = norm;
            rgba[idx + 2] = norm;
            rgba[idx + 3] = 255;
        }
    }
    return rgba;
}

/**
 * Map multi-band raster data (RGB or RGBA) to an RGBA image.
 * Values are clamped to 0-255 for 8-bit data; for wider types the range is
 * stretched the same way as single-band.
 * @internal Exported for testing.
 */
export function multiBandToRGBA(
    bands: TypedArray[],
    width: number,
    height: number,
    bitsPerSample: number,
): Uint8ClampedArray {
    if (bands.length === 2) {
        Logger.warn(`[GeoTIFF] 2-band image — rendering as R=band0, G=band1, B=0. Consider single-band mode for luminance+mask data.`);
    } else if (bands.length > 4) {
        Logger.warn(`[GeoTIFF] ${bands.length}-band image — only first 4 bands used for RGBA display.`);
    }
    const rgba = new Uint8ClampedArray(width * height * 4);
    const hasAlpha = bands.length >= 4;
    const is8bit = bitsPerSample === 8;

    // For non-8-bit data, compute per-band min/max for normalization
    const bandRanges: Array<{ min: number; max: number; range: number }> = [];
    if (!is8bit) {
        for (const band of bands) {
            let bMin = Infinity;
            let bMax = -Infinity;
            for (let i = 0; i < band.length; i++) {
                if (band[i] < bMin) bMin = band[i];
                if (band[i] > bMax) bMax = band[i];
            }
            if (!isFinite(bMin)) { bMin = 0; bMax = 1; }
            bandRanges.push({ min: bMin, max: bMax, range: bMax - bMin || 1 });
        }
    }

    const pixelCount = width * height;
    for (let i = 0; i < pixelCount; i++) {
        const idx = i * 4;
        for (let b = 0; b < 3; b++) {
            const raw = bands[b]?.[i] ?? 0;
            rgba[idx + b] = is8bit
                ? Math.min(255, Math.max(0, raw))
                : Math.round(((raw - bandRanges[b].min) / bandRanges[b].range) * 255);
        }
        rgba[idx + 3] = hasAlpha
            ? (is8bit ? Math.min(255, Math.max(0, bands[3][i])) : Math.round(((bands[3][i] - bandRanges[3].min) / bandRanges[3].range) * 255))
            : 255;
    }
    return rgba;
}

/** Read raster bands from a GeoTIFF image. */
async function readBands(image: GeoTIFFImage): Promise<TypedArray[]> {
    const samplesPerPixel = image.getSamplesPerPixel();
    const width = image.getWidth();
    const height = image.getHeight();
    const rasters = await image.readRasters({ interleave: false }) as ReadRasterResult;

    const bands: TypedArray[] = [];
    if (Array.isArray(rasters) && rasters.length > 0 && ArrayBuffer.isView(rasters[0])) {
        for (let i = 0; i < samplesPerPixel; i++) {
            bands.push((rasters as TypedArray[])[i]);
        }
    } else {
        // Defensive: readRasters({interleave:false}) should always return TypedArray[],
        // but handle a flat interleaved buffer in case of unusual TIFF variants.
        const flat = rasters as TypedArray;
        for (let i = 0; i < samplesPerPixel; i++) {
            const band = new (flat.constructor as { new (length: number): TypedArray })(width * height);
            for (let p = 0; p < width * height; p++) {
                band[p] = flat[p * samplesPerPixel + i];
            }
            bands.push(band);
        }
    }
    return bands;
}

/** Build RGBA from bands. */
function bandsToRGBA(bands: TypedArray[], width: number, height: number, bitsPerSample: number, noData: number | null): Uint8ClampedArray {
    return bands.length === 1
        ? singleBandToRGBA(bands[0], width, height, noData)
        : multiBandToRGBA(bands, width, height, bitsPerSample);
}

/** Extract geo-referencing metadata from a parsed GeoTIFF image. */
function extractMetadata(image: GeoTIFFImage): GeoTiffMetadata {
    let origin: number[] | null = null;
    let resolution: number[] | null = null;
    let bbox: number[] | null = null;
    try { origin = image.getOrigin(); } catch { /* no affine transform */ }
    try { resolution = image.getResolution(); } catch { /* no affine transform */ }
    try { bbox = image.getBoundingBox(); } catch { /* no affine transform */ }

    return {
        width: image.getWidth(),
        height: image.getHeight(),
        samplesPerPixel: image.getSamplesPerPixel(),
        bitsPerSample: image.getBitsPerSample(),
        geoKeys: image.getGeoKeys() as Record<string, unknown> | null,
        noData: image.getGDALNoData(),
        origin,
        resolution,
        bbox,
    };
}

// ── Public API ────────────────────────────────────────────────────────

/**
 * Lightweight decode: returns RGBA pixels + dimensions for texture display.
 * Does NOT retain raw band data — use `decodeGeoTiff()` for analysis.
 */
export async function decodeGeoTiffTexture(blob: Blob): Promise<GeoTiffTextureResult> {
    const image = await openImage(blob);
    const width = image.getWidth();
    const height = image.getHeight();
    const noData = image.getGDALNoData();
    const bands = await readBands(image);
    const rgba = bandsToRGBA(bands, width, height, image.getBitsPerSample(), noData);
    let isGeoReferenced = false;
    try { isGeoReferenced = image.getBoundingBox().length === 4; } catch { /* no affine transform */ }
    return { rgba, width, height, isGeoReferenced };
}

/**
 * Full decode: returns raw band data, geo-referencing metadata, and RGBA pixels.
 * Call on-demand from tooling — decodes fresh from the ResourceManager blob.
 */
export async function decodeGeoTiff(blob: Blob): Promise<GeoTiffResult> {
    const image = await openImage(blob);
    const metadata = extractMetadata(image);
    const bands = await readBands(image);
    const rgba = bandsToRGBA(bands, metadata.width, metadata.height, metadata.bitsPerSample, metadata.noData);
    return { rgba, bands, metadata };
}
